// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

// frontend/src/design-system/components/forms/Field.jsx

import React, { useId } from "react";
import "./forms.css";

function joinClassNames(...values) {
  return values.filter(Boolean).join(" ");
}

export default function Field({
  id,
  label,
  required = false,
  optionalLabel = "",
  help = "",
  error = "",
  fullWidth = true,
  className = "",
  children,
}) {
  const generatedId = useId();
  const controlId = id || `oasys-field-${generatedId.replace(/:/g, "")}`;
  const helpId = help ? `${controlId}-help` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(" ") || undefined;

  const control =
    typeof children === "function"
      ? children({
          id: controlId,
          required,
          "aria-invalid": error ? true : undefined,
          "aria-describedby": describedBy,
        })
      : React.isValidElement(children)
        ? React.cloneElement(children, {
            id: children.props.id || controlId,
            required: children.props.required ?? required,
            "aria-invalid": children.props["aria-invalid"] ?? (error ? true : undefined),
            "aria-describedby": children.props["aria-describedby"] || describedBy,
          })
        : children;

  return (
    <div
      className={joinClassNames(
        "oasys-field",
        fullWidth && "oasys-field--full-width",
        error && "oasys-field--error",
        className
      )}
    >
      {label ? (
        <div className="oasys-field__label-row">
          <label className="oasys-field__label" htmlFor={controlId}>
            {label}
            {required ? (
              <span className="oasys-field__required" aria-hidden="true">
                *
              </span>
            ) : null}
          </label>
          {!required && optionalLabel ? (
            <span className="oasys-field__optional">{optionalLabel}</span>
          ) : null}
        </div>
      ) : null}

      {control}

      {help ? (
        <p className="oasys-field__help" id={helpId}>
          {help}
        </p>
      ) : null}

      {error ? (
        <p className="oasys-field__error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
